// src/scrub.ts
var MAX_DEPTH = 6;
var NAVIGATION_KEYS = /* @__PURE__ */ new Set(["from", "to"]);
var SENSITIVE_KEY_FRAGMENTS = /(phone|mobile|msisdn|e_?mail|password|passwd|secret|token|api_?key|authori[sz]ation|cookie|postcode|address|translat)/i;
var SENSITIVE_KEYS = /^(to|cleaned_?to|recipients?|body|text|content|message_?body|sms_?body|(first|last|full|customer|contact|display|user)_?name)$/i;
var ABSOLUTE_URL = /https?:\/\/[^\s"'<>`]+/g;
var RELATIVE_PATH_QUERY = /(^|[\s("'=`])(\/[^\s?#"'<>`]*)\?[^\s#"'<>`]*/g;
var ACCESS_TOKEN_PATH = /(\/(?:campaign|workflows\/shares\/resolve)\/)[^/\s?#"'<>`]+/g;
var JWT = /\beyJ[\w-]+\.[\w-]+\.[\w-]+/g;
var AUTH_HEADER_VALUE = /\b(Bearer|Basic)\s+[A-Za-z0-9\-._~+/]+=*/g;
var SENSITIVE_ASSIGNMENT = /\b(password|passwd|pwd|secret|client_?secret|token|access_?token|refresh_?token|api_?key|access_?key|authorization|to|cleaned_?to|recipient_?number)(["']?\s*[:=]\s*["']?)([^\s"'&,;}]+)/gi;
var AWS_ACCESS_KEY = /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g;
var EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
var UK_PHONE = /(?<![\w+-])(?:\+?44\s?(?:\(0\)\s?)?|0)[1-9](?:[\s-]?\d){8,9}(?![\w-])/g;
var INTERNATIONAL_PHONE = /(?<![\w+-])\+[1-9](?:[\s-]?\d){7,14}(?![\w-])/g;
var UK_POSTCODE = /\b[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}\b/g;
var API_ACCESS_LOG_LINE = /^(<--|-->) [A-Z]+ \//;
var DATABASE_ERROR_TEXT = /Failed query:|DrizzleQueryError/;
var DATABASE_ERROR_MESSAGE = "Database query failed (details redacted)";
var DATABASE_LOG_METADATA = /* @__PURE__ */ new Set([
  "sentry.environment",
  "sentry.release",
  "sentry.sdk.name",
  "sentry.sdk.version",
  "sentry.origin",
  "sentry.timestamp.sequence",
  "consola.type",
  "consola.level"
]);
function getDatabaseErrorDetails(value, seen = /* @__PURE__ */ new WeakSet(), depth = 0) {
  if (typeof value === "string") {
    return DATABASE_ERROR_TEXT.test(value) ? { message: DATABASE_ERROR_MESSAGE } : void 0;
  }
  if (!value || typeof value !== "object" || seen.has(value) || depth >= MAX_DEPTH) {
    return void 0;
  }
  seen.add(value);
  const record = value;
  const code = typeof record.code === "string" && /^[0-9A-Z]{5}$/.test(record.code) ? record.code : void 0;
  const isDatabaseError = typeof record.query === "string" && Array.isArray(record.params) || code !== void 0 && typeof record.severity === "string";
  let details = isDatabaseError ? { message: DATABASE_ERROR_MESSAGE, ...code ? { code } : {} } : void 0;
  for (const item of [record.cause, record.message, ...Object.values(record)]) {
    const nested = getDatabaseErrorDetails(item, seen, depth + 1);
    if (nested) {
      details = details?.code ? details : nested;
    }
  }
  return details;
}
function scrubText(text) {
  if (DATABASE_ERROR_TEXT.test(text)) {
    return DATABASE_ERROR_MESSAGE;
  }
  return text.replace(ABSOLUTE_URL, (url) => sanitizeUrl(url) ?? "[url]").replace(RELATIVE_PATH_QUERY, "$1$2").replace(ACCESS_TOKEN_PATH, "$1[token]").replace(JWT, "[token]").replace(AUTH_HEADER_VALUE, "$1 [token]").replace(SENSITIVE_ASSIGNMENT, "$1$2[redacted]").replace(AWS_ACCESS_KEY, "[aws-key]").replace(EMAIL, "[email]").replace(UK_PHONE, "[phone]").replace(INTERNATIONAL_PHONE, "[phone]").replace(UK_POSTCODE, "[postcode]");
}
function sanitizeUrl(value) {
  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(value);
  try {
    const url = new URL(value, "http://relative.invalid");
    const pathname = url.pathname.replace(ACCESS_TOKEN_PATH, "$1[token]");
    return isAbsolute ? `${url.origin}${pathname}` : pathname;
  } catch {
    return void 0;
  }
}
function scrubAttributes(attributes) {
  return scrubValue(attributes, 0);
}
function scrubValue(value, depth) {
  if (typeof value === "string") {
    return scrubText(value);
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (depth >= MAX_DEPTH) {
    return "[truncated]";
  }
  if ("message" in value || "query" in value && "params" in value) {
    const databaseError = getDatabaseErrorDetails(value);
    if (databaseError) {
      return databaseError;
    }
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1));
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, scrubEntry(key, item, depth + 1)])
  );
}
function scrubEntry(key, value, depth) {
  if (NAVIGATION_KEYS.has(key) && typeof value === "string" && /^(\/|https?:)/.test(value)) {
    return sanitizeUrl(value) ?? "[url]";
  }
  return isSensitiveKey(key) ? "[redacted]" : scrubValue(value, depth);
}
function isSensitiveKey(key) {
  if (key.startsWith("sentry.")) {
    return false;
  }
  return SENSITIVE_KEYS.test(key) || SENSITIVE_KEY_FRAGMENTS.test(key);
}
function scrubLog(log) {
  const message = String(log.message);
  if (API_ACCESS_LOG_LINE.test(message)) {
    return null;
  }
  const databaseError = getDatabaseErrorDetails(log.attributes) ?? getDatabaseErrorDetails(message);
  if (databaseError) {
    return {
      ...log,
      message: databaseError.message,
      attributes: {
        ...scrubAttributes(Object.fromEntries(
          Object.entries(log.attributes ?? {}).filter(([key]) => DATABASE_LOG_METADATA.has(key))
        )),
        ...databaseError.code ? { "db.sqlstate": databaseError.code } : {}
      }
    };
  }
  const scrubbed = { ...log, message: scrubText(message) };
  if (log.attributes) {
    scrubbed.attributes = scrubAttributes(log.attributes);
  }
  return scrubbed;
}
function scrubTransaction(event) {
  if (event.transaction) {
    event.transaction = scrubText(event.transaction);
  }
  for (const span of event.spans ?? []) {
    if (span.description) {
      span.description = scrubText(span.description);
    }
    if (span.data) {
      span.data = scrubValue(span.data, 0);
    }
  }
  return event;
}
function scrubSpan(span) {
  span.name = scrubText(span.name);
  span.attributes = scrubAttributes(span.attributes);
  for (const link of span.links ?? []) {
    if (link.attributes) {
      link.attributes = scrubAttributes(link.attributes);
    }
  }
  return span;
}
function scrubFeedbackEvent(event) {
  const feedback = event.contexts?.feedback;
  if (event.type !== "feedback" || !feedback) {
    return event;
  }
  delete feedback.contact_email;
  delete feedback.name;
  if (typeof feedback.message === "string") {
    feedback.message = scrubText(feedback.message);
  }
  if (typeof feedback.url === "string") {
    const url = sanitizeUrl(feedback.url);
    if (url) {
      feedback.url = url;
    } else {
      delete feedback.url;
    }
  }
  return event;
}
function scrubReplayEvent(event) {
  if (event.type !== "replay_event") {
    return event;
  }
  const replayEvent = event;
  if (replayEvent.urls) {
    replayEvent.urls = replayEvent.urls.map((url) => sanitizeUrl(url) ?? "[url]");
  }
  if (replayEvent.request?.url) {
    replayEvent.request.url = sanitizeUrl(replayEvent.request.url) ?? "[url]";
  }
  return replayEvent;
}
var CUSTOM_RECORDING_EVENT = 5;
function scrubRecordingEvent(event) {
  if (event.type !== CUSTOM_RECORDING_EVENT || !event.data || typeof event.data !== "object") {
    return event;
  }
  const data = event.data;
  if (data.payload && typeof data.payload === "object") {
    data.payload = scrubValue(data.payload, 0);
  }
  return event;
}

export {
  getDatabaseErrorDetails,
  scrubText,
  sanitizeUrl,
  scrubAttributes,
  scrubLog,
  scrubTransaction,
  scrubSpan,
  scrubFeedbackEvent,
  scrubReplayEvent,
  scrubRecordingEvent
};
